// Badge — the one small inline label/tag primitive, replacing ~40 hand-rolled
// `rounded-full … ring-1 ring-white/10` chips scattered across pages. Tones map
// to the app's semantic palette; an optional leading icon and a soft/solid
// variant cover the common cases. For domain *status/severity* use StatusPill /
// PriorityPill (in primitives.tsx) which add a meaning-carrying icon — Badge is
// for neutral tags (category, type, count, policy name).
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export type BadgeTone = 'neutral' | 'info' | 'ok' | 'warning' | 'critical' | 'accent';
type Variant = 'soft' | 'outline' | 'solid';

const SOFT: Record<BadgeTone, string> = {
  neutral: 'bg-white/5 text-muted ring-white/10',
  info: 'bg-primary/10 text-primary ring-primary/30',
  ok: 'bg-ok/10 text-ok ring-ok/30',
  warning: 'bg-warning/10 text-warning ring-warning/30',
  critical: 'bg-critical/10 text-critical ring-critical/30',
  accent: 'bg-accent/10 text-accent ring-accent/30',
};
const OUTLINE: Record<BadgeTone, string> = {
  neutral: 'bg-transparent text-muted ring-white/15',
  info: 'bg-transparent text-primary ring-primary/40',
  ok: 'bg-transparent text-ok ring-ok/40',
  warning: 'bg-transparent text-warning ring-warning/40',
  critical: 'bg-transparent text-critical ring-critical/40',
  accent: 'bg-transparent text-accent ring-accent/40',
};
const SOLID: Record<BadgeTone, string> = {
  neutral: 'bg-white/15 text-fg ring-transparent',
  info: 'bg-primary text-white ring-transparent',
  ok: 'bg-ok text-white ring-transparent',
  warning: 'bg-warning text-black ring-transparent',
  critical: 'bg-critical text-white ring-transparent',
  accent: 'bg-accent text-white ring-transparent',
};

export function Badge({
  children, tone = 'neutral', variant = 'soft', icon: Icon, className = '', title,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  variant?: Variant;
  icon?: LucideIcon;
  className?: string;
  title?: string;
}) {
  const palette = variant === 'outline' ? OUTLINE : variant === 'solid' ? SOLID : SOFT;
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-caption font-medium ring-1 ${palette[tone]} ${className}`}
    >
      {Icon && <Icon size={11} className="shrink-0" />}
      {children}
    </span>
  );
}
